import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Message, Messages } from "capitalrx-components-2.0/lib/general";
import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body } from "capitalrx-components-2.0/lib/typography";
import { DateTime } from "luxon";

import { useListMedicarePrescriptionPaymentPlanDueInvoicesQuery } from "@/App/api/medicarePrescriptionPaymentPlan";
import {
  useIsMemberPrecludedFromParticipation,
  useIsParticipatingInMedicarePrescriptionPlan,
  useIsPendingParticipationInMedicarePrescriptionPaymentPlan,
  useMedicarePrescriptionPaymentPlanGracePeriod,
} from "@/App/hooks";
import { PlanYearProvider } from "@/App/pages";
import { AccumulationPanel } from "@/App/pages/MedicarePrescriptionPaymentPlan/Participation/Accumulation";

import { CommunicationPreferencesPanel } from "./CommunicationPreferences";
import { InvoiceHistoryPanel } from "./InvoiceHistory";
import { OptInForm } from "./OptInForm";
import { ParticipationDetailsPanel } from "./ParticipationDetails";
import { PaymentHistoryPanel } from "./PaymentHistory";
import { PaymentInformationPanel } from "./PaymentInformation";
import { UpcomingPaymentsPanel } from "./UpcomingPayments";

interface ParticipationTabProps {
  readonly planYear: number;
}

export const ParticipationTab: FC<ParticipationTabProps> = ({ planYear }) => {
  const { isParticipating } = useIsParticipatingInMedicarePrescriptionPlan(planYear);
  const { isPendingParticipation } = useIsPendingParticipationInMedicarePrescriptionPaymentPlan(planYear);
  const { isInGracePeriod, gracePeriodTerminationDate } = useMedicarePrescriptionPaymentPlanGracePeriod(planYear);
  const { isInvolutarilyDisenrolled } = useIsMemberPrecludedFromParticipation(planYear);

  const { data: dueInvoices, isLoading: isLoadingDueInvoices } = useListMedicarePrescriptionPaymentPlanDueInvoicesQuery(planYear);
  const { t: translate } = useTranslation();

  const TRANSLATION_PATH = "MEDICARE_PRESCRIPTION_PAYMENT_PLAN.participationTab";

  const hasDueInvoices = useMemo(() => {
    return !isLoadingDueInvoices && dueInvoices.results.length > 0;
  }, [isLoadingDueInvoices, dueInvoices]);

  const dueDateOfOverdueInvoice = useMemo(() => {
    const latestInvoiceDueDate = (dueInvoices?.results || [])
      .map((invoice) => DateTime.fromISO(invoice.due_date))
      .sort((a, b) => {
        return b.diff(a).milliseconds;
      })[0];

    if (latestInvoiceDueDate) {
      return latestInvoiceDueDate.toUTC().toFormat("MM/dd/yyyy");
    } else {
      return "--";
    }
  }, [dueInvoices]);

  return (
    <PlanYearProvider planYear={planYear}>
      <Stack spacing="large" testId={"participation-tab-" + planYear}>
        {isParticipating && !isInGracePeriod && (
          <Message visible={hasDueInvoices} appearance={Messages.Warning}>
            <Body>{translate(`${TRANSLATION_PATH}.outstandingBalance`)}</Body>
          </Message>
        )}
        {isParticipating && isInGracePeriod && (
          <Message visible={hasDueInvoices} appearance={Messages.Error}>
            <Body>
              {translate(`${TRANSLATION_PATH}.outstandingBalanceGracePeriod`)
                .replaceAll("{invoiceDueDate}", dueDateOfOverdueInvoice)
                .replaceAll("{gracePeriodTermination}", DateTime.fromISO(gracePeriodTerminationDate).toUTC().toFormat("MM/dd/yyyy"))}
            </Body>
          </Message>
        )}
        {!isParticipating && !isInvolutarilyDisenrolled && (
          <Message visible={hasDueInvoices} appearance={Messages.Warning}>
            <Body>{translate(`${TRANSLATION_PATH}.outstandingBalanceVoluntarilyDisenrolled`)}</Body>
          </Message>
        )}
        {!isParticipating && isInvolutarilyDisenrolled && (
          <Message visible={hasDueInvoices} appearance={Messages.Error}>
            <Body>{translate(`${TRANSLATION_PATH}.outstandingBalanceInvoluntarilyDisenrolled`)}</Body>
          </Message>
        )}
        {!isParticipating && !isPendingParticipation && <OptInForm />}
        {(isParticipating || isPendingParticipation) && <ParticipationDetailsPanel />}
        <PaymentInformationPanel />

        <AccumulationPanel />
        <UpcomingPaymentsPanel />
        <PaymentHistoryPanel />
        <InvoiceHistoryPanel />
        <CommunicationPreferencesPanel />
      </Stack>
    </PlanYearProvider>
  );
};
